import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { HOMER_PAGE_CONTEXT_MAX_LENGTH } from '@padspin/function-types';
import { filter, map, startWith } from 'rxjs/operators';

/** Route `data` key holding the resolved listing (set by `ListingResolver`). */
export const HOMER_LISTING_ROUTE_DATA_KEY = 'listing';

/** Format a single context value; returns null for values not worth sending. */
function formatValue(v: unknown): string | null {
  if (v === undefined || v === null || v === '') return null;
  if (v instanceof Date)
    return isNaN(v.getTime()) ? null : v.toISOString().slice(0, 10);
  const t = typeof v;
  if (t === 'string' || t === 'number' || t === 'boolean') return String(v);
  return null; // skip objects/arrays rather than emit "[object Object]"
}

/** Compact, readable "k: v, k: v" for the useful, non-empty fields of an object. */
function formatKeyValues(data: Record<string, unknown>): string {
  return Object.entries(data)
    .map(([k, v]) => [k, formatValue(v)] as const)
    .filter((pair): pair is readonly [string, string] => pair[1] !== null)
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
    .join(', ');
}

const LISTING_DESCRIPTION_MAX_LENGTH = 600;

const LISTING_FEATURES: readonly [key: string, label: string][] = [
  ['washer_dryer', 'in-unit laundry'],
  ['dishwasher', 'dishwasher'],
  ['parking', 'parking'],
  ['outdoor', 'private outdoor space'],
  ['renovated', 'renovated'],
  ['elevator', 'elevator'],
  ['doorman', 'doorman'],
  ['gym', 'gym'],
  ['pool', 'pool'],
];

const LISTING_UTILITIES: readonly [key: string, label: string][] = [
  ['utility_heat_included', 'heat'],
  ['utility_water_included', 'water'],
  ['utility_gas_included', 'gas'],
  ['utility_electric_included', 'electricity'],
  ['utility_wifi_included', 'wifi'],
  ['utility_trash_included', 'trash'],
  ['utility_sewage_included', 'sewage'],
  ['utility_parking_included', 'parking'],
  ['utility_storage_included', 'storage'],
  ['utility_gym_included', 'gym'],
  ['utility_cable_included', 'cable'],
];

/** Plain text of a markdown description, shortened to fit the context budget. */
function plainDescription(markdown: unknown): string | null {
  if (typeof markdown !== 'string') return null;
  const text = markdown
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/<[^>]+>/g, ' ')
    .replace(/[#*_`>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return null;
  return text.length > LISTING_DESCRIPTION_MAX_LENGTH
    ? `${text.slice(0, LISTING_DESCRIPTION_MAX_LENGTH - 1)}…`
    : text;
}

function labelsWhere(
  l: Record<string, unknown>,
  pairs: readonly (readonly [string, string])[],
  value: boolean,
): string | null {
  const labels = pairs
    .filter(([k]) => l[k] === value)
    .map(([, label]) => label);
  return labels.length ? labels.join(', ') : null;
}

/**
 * A summary of the current listing for Homer, or null if none: enough for a
 * renter's questions about the unit, and the address and unit number so
 * answers about one listing can be told apart from another.
 */
function summarizeListing(listing: unknown): string | null {
  if (!listing || typeof listing !== 'object') return null;
  const l = listing as Record<string, unknown>;
  const unknown = Array.isArray(l['unknown_facts']) ? l['unknown_facts'] : [];
  const summary = formatKeyValues({
    address: l['address'] ?? l['street_address'],
    unit: l['unit_number'],
    neighborhood: l['neighborhood'],
    city: l['city'],
    rent: l['rent_amount'] ?? l['rent'],
    'monthly add-on expenses': l['rent_addon_expenses'],
    'move-in fee': l['move_in_fee'],
    bedrooms: l['bedrooms'],
    bathrooms: unknown.includes('bathrooms') ? 'not stated' : l['bathrooms'],
    'square feet': l['area'],
    floor: l['floor'],
    type: l['type'] ?? l['property_type'],
    'available from': l['available_from_date'] ?? l['available_from'],
    'pets allowed': unknown.includes('pets')
      ? 'not stated'
      : typeof l['pets'] === 'boolean'
        ? l['pets']
          ? 'yes'
          : 'no'
        : null,
    has: labelsWhere(l, LISTING_FEATURES, true),
    'does not have': labelsWhere(l, LISTING_FEATURES, false),
    'utilities included': labelsWhere(l, LISTING_UTILITIES, true),
    description: plainDescription(l['description']),
  });
  return summary || null;
}

/** "post_a_pad" -> "Post a pad", for the context label. */
function humanizeSource(source: string): string {
  const words = source.replace(/[_-]/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Merge the route label, listing summary, and page contributions into one
 * compact context string bounded to {@link HOMER_PAGE_CONTEXT_MAX_LENGTH}, or
 * `null` when there is nothing to say. Pure and SSR-safe (no browser globals).
 */
export function buildHomerPageContext(
  label: string | null,
  listing: unknown,
  contributions: Record<string, Record<string, unknown>>,
): string | null {
  const sections: string[] = [];
  if (label) sections.push(label);
  const listingSummary = summarizeListing(listing);
  if (listingSummary) sections.push(`Listing — ${listingSummary}`);
  for (const [source, data] of Object.entries(contributions)) {
    const kv = formatKeyValues(data);
    if (kv) sections.push(`${humanizeSource(source)} — ${kv}`);
  }
  if (sections.length === 0) return null;

  const joined = sections.join('. ');
  return joined.length > HOMER_PAGE_CONTEXT_MAX_LENGTH
    ? `${joined.slice(0, HOMER_PAGE_CONTEXT_MAX_LENGTH - 1)}…`
    : joined;
}

/**
 * Route `data` key a page sets to describe itself to Homer, e.g.
 * `data: { homerContext: 'the search results page' }`. Inherited by child
 * routes (the router is configured with `paramsInheritanceStrategy: 'always'`).
 */
export const HOMER_CONTEXT_ROUTE_DATA_KEY = 'homerContext';

/**
 * Route `data` flag a page sets to hide Homer on its route, e.g.
 * `data: { homerDisabled: true }`. Preferred over the imperative
 * {@link HomerChatService.disable} when a whole route should never show the
 * widget (e.g. the comp report, which is screenshotted into PDFs), because it
 * needs no dependency on this lib.
 */
export const HOMER_DISABLED_ROUTE_DATA_KEY = 'homerDisabled';

/** FAB placement token. `am-dashboard` shifts the FAB left of a page's own
 * bottom-right SpeedDial so the two don't overlap. */
export type HomerFabPosition = 'default' | 'am-dashboard';

/**
 * Route `data` key a page sets to move the FAB out of the way of its own
 * bottom-right UI, e.g. `data: { homerFabPosition: 'am-dashboard' }`. Defaults
 * to `'default'`; inherited by child routes. Keeps page-specific placement with
 * the page, not baked into the widget.
 */
export const HOMER_FAB_POSITION_ROUTE_DATA_KEY = 'homerFabPosition';

/**
 * Site-wide control surface for the Homer chat widget — the analogue of
 * `HeaderService` / `FooterService`. A page opts the widget out for its route by
 * calling {@link disable} in `ngOnInit`; visibility resets on every navigation.
 * Also resolves the current page's Homer context and the FAB placement.
 */
@Injectable({ providedIn: 'root' })
export class HomerChatService {
  private readonly router = inject(Router);

  private readonly isEnabled = signal(true);

  /** Whether the widget may render on the current route (imperative + route-flag). */
  readonly enabled = computed(() => this.isEnabled() && !this.routeDisabled());

  /**
   * Per-source page context contributed by live pages (form values etc.), keyed
   * by a stable source name. Merged into {@link pageContext}. Cleared on every
   * navigation so one page's context never leaks into the next.
   */
  private readonly contributions = signal<
    Record<string, Record<string, unknown>>
  >({});

  constructor() {
    // Re-enable + clear contributed context on every navigation; a page that
    // wants Homer hidden re-asserts disable() in ngOnInit (mirrors FooterService).
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.isEnabled.set(true);
        this.contributions.set({});
      });
  }

  /** Hide the widget for the current route (call from a page's `ngOnInit`). */
  disable(): void {
    this.isEnabled.set(false);
  }

  /**
   * Contribute live page context Homer can use — e.g. a page's form values.
   * Pass `null` to clear a source. Keyed by `source` so multiple pages/widgets
   * don't collide; automatically cleared on navigation. Contribute only the
   * user's own data and only useful fields (never files/signatures/secrets).
   */
  setContext(source: string, data: Record<string, unknown> | null): void {
    this.contributions.update((current) => {
      const next = { ...current };
      if (data === null) delete next[source];
      else next[source] = data;
      return next;
    });
  }

  private readonly navEnd = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /**
   * The context sent with each message: the route's `homerContext` label, a
   * compact summary of the resolved listing (booking/listing pages), and any
   * live page contributions — merged and bounded to
   * {@link HOMER_PAGE_CONTEXT_MAX_LENGTH}. `null` when there is nothing to say.
   */
  readonly pageContext = computed<string | null>(() => {
    // Depend on navEnd + contributions so this recomputes on navigation/edits.
    this.navEnd();
    const contributions = this.contributions();

    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    let label: string | null = null;
    let listing: unknown;
    while (route) {
      const value = route.data[HOMER_CONTEXT_ROUTE_DATA_KEY];
      if (typeof value === 'string') label = value;
      if (route.data[HOMER_LISTING_ROUTE_DATA_KEY] != null) {
        listing = route.data[HOMER_LISTING_ROUTE_DATA_KEY];
      }
      route = route.firstChild;
    }

    return buildHomerPageContext(label, listing, contributions);
  });

  /** Whether the current route opts out of Homer via `data.homerDisabled`. */
  private readonly routeDisabled = computed<boolean>(() => {
    this.navEnd();
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    while (route) {
      if (route.data[HOMER_DISABLED_ROUTE_DATA_KEY] === true) return true;
      route = route.firstChild;
    }
    return false;
  });

  /** FAB placement for the current route, from `data.homerFabPosition`. */
  readonly position = computed<HomerFabPosition>(() => {
    this.navEnd();
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    let position: HomerFabPosition = 'default';
    while (route) {
      const value = route.data[HOMER_FAB_POSITION_ROUTE_DATA_KEY];
      if (value === 'am-dashboard' || value === 'default') position = value;
      route = route.firstChild;
    }
    return position;
  });
}
